// src/index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import { loadSentry } from './sentryClient';
import './i18n'; // EMİR #79-C — i18n init (tek locale tr; görünür değişiklik yok)
import './index.css';
import App from './App';
import AppErrorBoundary from './components/AppErrorBoundary';
import reportWebVitals from './reportWebVitals';
import { sendWebVital } from './services/webVitals';
import { startAutoUpdate } from './swAutoUpdate';
import { installImageFallback } from './imageFallback';

// PR-17 — permalinkBoot/PermalinkPage kaldırıldı: prod'da /p/** ve /c/**
// hosting rewrite'ları renderPostShare/renderClipShare'e gider ve insan
// trafiğini /?p= | /?c='ye yönlendirir; SPA bu pathname'lerle hiç boot
// olmuyordu. Kalan tek yol (offline-SW / dev) artık App'in kendi
// resolvePathToPage /p/{id} + /c/{id} matcher'larıyla çözülür.

// ============================================================
// ChunkLoadError koruması — her hosting deploy'unda chunk hash'leri
// değişir; açık kalan ESKİ sekme yeni navigasyonda eski chunk'ı arar,
// sunucu index.html döner (MIME text/html) ve sayfa BEYAZ kalır.
// Çözüm: chunk yükleme hatasında sayfayı 1 kez otomatik yenile
// (sessionStorage bayrağı sonsuz reload döngüsünü engeller).
// ============================================================
// DENETİM FIX: sessionStorage erişimi fırlatan ortamlarda (bazı gizli mod /
// kısıtlı storage) bayrak yazılamayıp SONSUZ reload döngüsü oluşabiliyordu —
// bellek-içi yedek bayrak eklendi (reload'lar arası yaşamaz ama art arda
// hızlı döngüyü keser; storage'lı ortamlarda davranış aynı).
let chunkReloadedInMemory = false;
function reloadOnceForChunkError() {
  if (chunkReloadedInMemory) return;
  chunkReloadedInMemory = true;
  try {
    if (sessionStorage.getItem('mylasa-chunk-reload') === '1') return;
    sessionStorage.setItem('mylasa-chunk-reload', '1');
  } catch { /* private mode — bellek bayrağı yeterli */ }
  window.location.reload();
}
window.addEventListener('error', (event) => {
  const msg = String(event?.error?.name || event?.message || '');
  if (!msg.includes('ChunkLoadError') && !msg.includes('Loading chunk')) return;
  reloadOnceForChunkError();
});
window.addEventListener('unhandledrejection', (event) => {
  const msg = String(event?.reason?.name || event?.reason?.message || '');
  if (!msg.includes('ChunkLoadError') && !msg.includes('Loading chunk')) return;
  reloadOnceForChunkError();
});
// Sayfa sağlıklı açıldıysa bayrağı sıfırla (bir SONRAKİ deploy'da
// koruma yeniden çalışabilsin).
window.addEventListener('load', () => {
  setTimeout(() => {
    try { sessionStorage.removeItem('mylasa-chunk-reload'); } catch { /* no-op */ }
  }, 10000);
});

// ============================================================
// Sentry — Hata takip sistemi
// DSN: mylasa.sentry.io (EU region)
// Sadece production'da aktif; development'da devre dışı.
// ============================================================
if (process.env.NODE_ENV === 'production') {
  // PERF: Sentry init'i KRİTİK BOOT yolundan çıkar — observability, ilk
  // render'ı geciktirmemeli. requestIdleCallback (yoksa setTimeout) ile
  // boşta başlat → ilk boyamadan sonra kurulur, kullanıcı-görünür etki yok.
  const startSentry = () => {
    // PERF: @sentry/react dynamic import (main chunk'tan ayrı "sentry" chunk).
    loadSentry().then((Sentry) => {
      if (!Sentry) return;
      Sentry.init({
        dsn: 'https://65d6aabd54c16eb9a9fbf865d6945503@o4511332203233280.ingest.de.sentry.io/4511332210573392',
        environment: 'production',
        // PR-7 — release = build'e gömülen git short-SHA (package.json build
        // script'i enjekte eder; CI REACT_APP_BUILD_SHA verirse ona saygı
        // duyulur). Crash → hangi deploy/commit eşleşmesi artık net.
        release: process.env.REACT_APP_BUILD_SHA || process.env.REACT_APP_VERSION || 'local-dev',
        // Örnekleme: her 10 işlemden 1'ini izle (performans maliyeti düşük)
        tracesSampleRate: 0.1,
        // Session replay: hata anında %100, normal %5
        replaysOnErrorSampleRate: 1.0,
        replaysSessionSampleRate: 0.05,
        // Kişisel veri gönderme (IP adresi vb.) — kapalı
        sendDefaultPii: false,
        // Breadcrumb filtresi: console.log spam'ini engelle
        beforeBreadcrumb(breadcrumb) {
          if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
            return null;
          }
          return breadcrumb;
        },
      });
      // PR-21 — Friend-test debug etiketleri. NOT: build dev-branch
      // commit'inde yapılır; squash-merge sonrası o SHA main'de erişilmez
      // olabilir → build_time kurtarıcı sıralama anahtarıdır (hangi deploy
      // penceresi olduğunu kesin söyler). Hepsi fallback'li — env yoksa
      // 'unknown', app asla kırılmaz. "Hangi ekran" bilgisi ayrıca SDK'nın
      // otomatik event URL alanından gelir (her yüzey URL'li — PR-18).
      try {
        Sentry.setTags({
          build_sha: process.env.REACT_APP_BUILD_SHA || 'unknown',
          build_time: process.env.REACT_APP_BUILD_TIME || 'unknown',
          app_version: process.env.REACT_APP_VERSION || 'unknown',
        });
      } catch { /* observability asla boot'u kırmaz */ }
    });
  };
  if (typeof (window as any).requestIdleCallback === 'function') {
    (window as any).requestIdleCallback(startSentry, { timeout: 4000 });
  } else {
    setTimeout(startSentry, 2000);
  }
}

// EMİR #88.2 — iOS Safari CSS :active'i YALNIZ sayfada bir touch dinleyicisi
// varsa tetikler. Boş, passive body-level touchstart dinleyicisi .pressable
// press-scale'inin iOS'ta çalışmasını sağlar (Android'de zaten çalışır; zararsız).
try { document.addEventListener('touchstart', () => {}, { passive: true }); } catch (_) { /* no-op */ }

// EMİR #91.2 — kırık-resim global fallback (çirkin "yırtık resim" glifi yerine
// markalı placeholder; kilitli mlv3 poster ATLANIR, bileşen onError'ları EZİLMEZ).
try { installImageFallback(); } catch (_) { /* no-op */ }

const rootEl = document.getElementById('root');
if (rootEl) {
  const root = ReactDOM.createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <AppErrorBoundary variant="full" label="root">
        <App />
      </AppErrorBoundary>
    </React.StrictMode>
  );
}

// Performans ölçümü — saha RUM (CLS/INP/LCP/FCP/TTFB) Firebase Analytics'e.
// Önceden argümansız çağrılıyordu → hiçbir şey ölçülmüyordu.
reportWebVitals(sendWebVital);

// === EMİR 1: Service Worker — Development'da devre dışı ===
if ('serviceWorker' in navigator) {
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isDevBuild = process.env.NODE_ENV === 'development';
  
  if (isDev || isDevBuild) {
    // Development: eski cache'li bundle yüklenmesini önle
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((reg) => {
        reg.unregister();
      });
    });
  } else {
    // Production: service worker aktif
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .then((reg) => {
          // Yeni sürüm var mı diye hemen ve periyodik kontrol ettir.
          try { reg.update(); } catch (_) {}
        })
        .catch((err) => {
          console.warn('[FLOW] Service worker register failed:', err);
        });
    });
  }
}

// App-side oto-güncelleme nöbetçisi — SW'den bağımsız ikinci katman.
// Yeni deploy'u (asset-manifest hash değişimi) görünce sayfayı bir kez yeniler.
startAutoUpdate();
